import type {
  ContractType,
  State,
  Timeframe,
  TradeDirection,
  TradeDurationUnit,
  TradeMarker,
  TradeStatus,
} from "./types";
import { priceToY, timeToX } from "./transformation";
import { getTheme } from "./theme";
import { markStaticDirty } from "./dirty";
import { getAutoPricePadding, getMinimumAutoPriceRange } from "./price";

function rgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const n = Number.parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}


const GREEN = "#16a34a";
const RED = "#dc2626";
const RESULT_LINGER_MS = 2500;

// Add this at the top of trades.ts
let settledDigitTimeout: number | null = null;
let settledDigitData: {
  digit: number;
  contractType: string;
  digitTarget?: number;
  digitLow?: number;
  digitHigh?: number;
  result: "won" | "lost";
  price: number;
  settledAt: number;
} | null = null;

/**
 * Show the settled digit indicator for a short period
 */
export function showSettledDigit(
  price: number,
  contractType: string,
  result: "won" | "lost",
  digitTarget?: number,
  digitLow?: number,
  digitHigh?: number,
  durationMs: number = 2000
): void {
  // Clear any existing timeout
  if (settledDigitTimeout !== null) {
    clearTimeout(settledDigitTimeout);
    settledDigitTimeout = null;
  }
  
  // Store the settled digit data
  settledDigitData = {
    digit: getFinalDigit(price),
    contractType,
    digitTarget,
    digitLow,
    digitHigh,
    result,
    price,
    settledAt: Date.now()
  };
  
  // Auto-clear after duration
  settledDigitTimeout = window.setTimeout(() => {
    settledDigitData = null;
    settledDigitTimeout = null;
    // Trigger a redraw
    if (typeof window !== 'undefined' && (window as any).__chartRedraw) {
      (window as any).__chartRedraw();
    }
  }, durationMs);
}

type TradePoint = { timeMs: number; price: number };
if (!CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function(
    x: number,
    y: number,
    w: number,
    h: number,
    radii: number | number[]
  ): void {
    const r = typeof radii === 'number' ? radii : (Array.isArray(radii) ? radii[0] || 0 : 0);
    const radius = Math.min(r, Math.min(w, h) / 2);
    
    this.beginPath();
    this.moveTo(x + radius, y);
    this.lineTo(x + w - radius, y);
    this.quadraticCurveTo(x + w, y, x + w, y + radius);
    this.lineTo(x + w, y + h - radius);
    this.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    this.lineTo(x + radius, y + h);
    this.quadraticCurveTo(x, y + h, x, y + h - radius);
    this.lineTo(x, y + radius);
    this.quadraticCurveTo(x, y, x + radius, y);
    this.closePath();
  };
}

export type ContractMarkerInput = {
  contractId?: string | number;
  contractType: ContractType;
  direction?: TradeDirection;
  duration: number;
  durationUnit?: TradeDurationUnit;
  entryPrice?: number;
  entryTimeEpoch?: number;
  barrier?: number;
  barrierHigh?: number;
  barrierLow?: number;
  digitTarget?: number;
  digitLow?: number;
  digitHigh?: number;
  expiryTimeEpoch?: number;
};

export type ContractLifecycleMessage = {
  type?: string;
  contract_id?: string | number;
  contract_type?: string;
  duration?: number;
  duration_ticks?: number;
  duration_unit?: string;
  entry_price?: number;
  entry_time_epoch?: number;
  activation_price?: number;
  activation_time_epoch?: number;
  expiry_time_epoch?: number;
  settled_time_epoch?: number;
  exit_price?: number;
  result?: string;
  symbol?: string;
  barrier?: number;
  barrier_high?: number;
  barrier_low?: number;
  digit_target?: number;
  digit_low?: number;
  digit_high?: number;
  settlement_tick_id?: string;
};

export type ContractLifecycleEnvelope = {
  code?: number;
  status?: string;
  message?: ContractLifecycleMessage;
};

export function shouldUseTickTimeframe(durationMs: number): boolean {
  return durationMs < 60000; // Less than 60 seconds
}


export function getRecommendedTimeframe(durationMs: number): Timeframe {
  return durationMs < 60000 ? "1t" : "1m";
}


function normalizeContractType(contractType: ContractType | undefined): string {
  const raw = String(contractType || "RISE").trim().toUpperCase();
  const normalized = raw.replace(/[\s-]+/g, "_").replace(/\//g, "_");
  const aliases: Record<string, string> = {
    RISE_FALL: "RISE_FALL",
    UP_DOWN: "UP_DOWN",
    CALL_PUT: "CALL_PUT",
    HIGH_LOW: "HIGH_LOW",
    TICKS: "TICKS",
    DIGITS: "DIGITS",
  };
  return aliases[normalized] ?? normalized;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeContractId(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return undefined;
    return String(Math.trunc(value));
  }
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeDurationUnit(durationUnit: TradeDurationUnit | string | undefined): TradeDurationUnit {
  const unit = String(durationUnit ?? "s").trim().toLowerCase();
  if (unit === "t" || unit === "tick" || unit === "ticks") return "ticks";
  if (unit === "s" || unit === "sec" || unit === "second" || unit === "seconds") return "s";
  if (unit === "m" || unit === "min" || unit === "minute" || unit === "minutes") return "m";
  if (unit === "h" || unit === "hour" || unit === "hours") return "h";
  return "s";
}

function normalizeDigitRange(input: ContractMarkerInput | ContractLifecycleMessage): { low?: number; high?: number } {
  const low = "digitLow" in input
    ? input.digitLow
    : "digit_low" in input
      ? input.digit_low
      : undefined;
  const high = "digitHigh" in input
    ? input.digitHigh
    : "digit_high" in input
      ? input.digit_high
      : undefined;
  return {
    low: isFiniteNumber(low) ? Math.trunc(low) : undefined,
    high: isFiniteNumber(high) ? Math.trunc(high) : undefined,
  };
}

export function durationToMs(duration: number, durationUnit: TradeDurationUnit = "s"): number {
  const value = Math.max(1, Math.floor(Number(duration) || 1));
  const unit = normalizeDurationUnit(durationUnit);
  if (unit === "t" || unit === "tick" || unit === "ticks" || unit === "s" || unit === "sec" || unit === "second" || unit === "seconds") {
    return value * 1_000;
  }
  if (unit === "m" || unit === "min" || unit === "minute" || unit === "minutes") return value * 60_000;
  if (unit === "h" || unit === "hour" || unit === "hours") return value * 3_600_000;
  return value * 1_000;
}

function normalizeEpochMs(value: number | undefined): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return value < 10_000_000_000 ? value * 1000 : value;
}

function getTradePathPoints(state: State, trade: TradeMarker, toTimeMs: number): TradePoint[] {
  const pts: TradePoint[] = [{ timeMs: trade.entryTime, price: trade.entryPrice }];
  for (const p of state.chartData) {
    const t = p.epoch * 1000;
    if (t > trade.entryTime && t <= toTimeMs) pts.push({ timeMs: t, price: p.quote });
  }
  if (pts[pts.length - 1].timeMs < toTimeMs) {
    pts.push({
      timeMs: toTimeMs,
      price: trade.exitPrice ?? state.chartData[state.chartData.length - 1]?.quote ?? trade.entryPrice,
    });
  }
  return pts;
}

function touchesBarrier(price: number, barrier: number, previousPrice?: number): boolean {
  if (price === barrier) return true;
  if (previousPrice == null) return false;
  return (previousPrice < barrier && price > barrier) || (previousPrice > barrier && price < barrier);
}

function isWithin(price: number, low: number, high: number): boolean {
  return price >= Math.min(low, high) && price <= Math.max(low, high);
}

function getFinalDigit(price: number, pipSize: number = 3): number {
  const precision = Number.isFinite(pipSize) ? Math.max(0, Math.min(15, Math.trunc(pipSize))) : 3;
  // Keep trailing zeroes so prices like 988.680 still resolve to a final digit of 0.
  const normalized = Math.abs(price).toFixed(precision).replace(".", "");
  return Number(normalized.at(-1) ?? 0);
}

function validateContractMarker(input: ContractMarkerInput): void {
  const type = normalizeContractType(input.contractType);
  if (!Number.isFinite(input.duration) || input.duration <= 0) throw new Error("Contract marker duration must be greater than 0.");

  if (type === "HIGH" || type === "LOW" || type === "KNOCK_IN" || type === "KNOCK_OUT") {
    if (!isFiniteNumber(input.barrier)) {
      throw new Error(`${type} contract markers require an absolute barrier price.`);
    }
  }

  if (type === "END_BETWEEN" || type === "ENDS_BETWEEN" || type === "ENDS_OUTSIDE" || type === "STAYS_IN" || type === "STAYS_OUT") {
    if (!isFiniteNumber(input.barrierLow) || !isFiniteNumber(input.barrierHigh)) {
      throw new Error(`${type} contract markers require barrierLow and barrierHigh prices.`);
    }
    if (input.barrierLow > input.barrierHigh) {
      throw new Error(`${type} contract markers require barrierLow to be less than or equal to barrierHigh.`);
    }
  }

  if (type === "MATCHES" || type === "DIFFERS" || type === "OVER" || type === "UNDER" || type === "DIGITS") {
    const { low, high } = normalizeDigitRange(input);
    if (low !== undefined && high !== undefined && low > high) {
      throw new Error(`${type} contract markers require digitLow to be less than or equal to digitHigh.`);
    }
  }
}

function isEven(num: number): boolean {
  return num % 2 === 0;
}

function isOdd(num: number): boolean {
  return num % 2 !== 0;
}

function isPrime(num: number): boolean {
  if (num < 2) return false;
  if (num === 2) return true;
  if (num % 2 === 0) return false;
  for (let i = 3; i <= Math.sqrt(num); i += 2) {
    if (num % i === 0) return false;
  }
  return true;
}

function isNonPrime(num: number): boolean {
  return !isPrime(num);
}

function resolveByContractType(trade: TradeMarker, exitPrice: number, path: TradePoint[]): TradeStatus {
  const type = normalizeContractType(trade.contractType);
  const entry = trade.entryPrice;
  const barrier = trade.barrier;
  const low = trade.barrierLow;
  const high = trade.barrierHigh;
  const digitTarget = trade.digitTarget;
  const digitLow = trade.digitLow;
  const digitHigh = trade.digitHigh;

  if (type === "UP") return exitPrice > entry ? "won" : "lost";
  if (type === "DOWN") return exitPrice < entry ? "won" : "lost";
  if (type === "RISE_FALL" || type === "UP_DOWN" || type === "CALL_PUT") {
    return trade.direction === "buy" ? (exitPrice > entry ? "won" : "lost") : (exitPrice < entry ? "won" : "lost");
  }
  if (type === "HIGH") return typeof barrier === "number" && exitPrice > barrier ? "won" : "lost";
  if (type === "LOW") return typeof barrier === "number" && exitPrice < barrier ? "won" : "lost";
  if (type === "END_BETWEEN" || type === "ENDS_BETWEEN") {
    return typeof low === "number" && typeof high === "number" && isWithin(exitPrice, low, high) ? "won" : "lost";
  }
  if (type === "ENDS_OUTSIDE") {
    return typeof low === "number" && typeof high === "number" && !isWithin(exitPrice, low, high) ? "won" : "lost";
  }
  if (type === "MATCHES") {
    const target = digitTarget ?? (typeof barrier === "number" ? Math.trunc(barrier) : undefined);
    if (typeof target !== "number") return exitPrice > entry ? "won" : "lost";
    return getFinalDigit(exitPrice) === target ? "won" : "lost";
  }
  if (type === "DIFFERS") {
    const target = digitTarget ?? (typeof barrier === "number" ? Math.trunc(barrier) : undefined);
    if (typeof target !== "number") return exitPrice < entry ? "won" : "lost";
    return getFinalDigit(exitPrice) !== target ? "won" : "lost";
  }
  if (type === "EVEN") {
    return isEven(getFinalDigit(exitPrice)) ? "won" : "lost";
  }
  if (type === "ODD") {
    return isOdd(getFinalDigit(exitPrice)) ? "won" : "lost";
  }
  if (type === "PRIME") {
    return isPrime(getFinalDigit(exitPrice)) ? "won" : "lost";
  }
  if (type === "NON_PRIME") {
    return isNonPrime(getFinalDigit(exitPrice)) ? "won" : "lost";
  }
  if (type === "OVER") {
    const target = digitTarget ?? (typeof barrier === "number" ? Math.trunc(barrier) : 5);
    return getFinalDigit(exitPrice) > target ? "won" : "lost";
  }
  if (type === "UNDER") {
    const target = digitTarget ?? (typeof barrier === "number" ? Math.trunc(barrier) : 5);
    return getFinalDigit(exitPrice) < target ? "won" : "lost";
  }
  if (type === "RANGE_IN") {
    const lastDigit = getFinalDigit(exitPrice);
    const rangeLow = digitLow ?? low ?? 0;
    const rangeHigh = digitHigh ?? high ?? 9;
    return lastDigit >= rangeLow && lastDigit <= rangeHigh ? "won" : "lost";
  }
  if (type === "RANGE_OUT") {
    const lastDigit = getFinalDigit(exitPrice);
    const rangeLow = digitLow ?? low ?? 0;
    const rangeHigh = digitHigh ?? high ?? 9;
    return lastDigit < rangeLow || lastDigit > rangeHigh ? "won" : "lost";
  }
  if (type === "KNOCK_IN") return didTouchSingleBarrier(path, barrier) ? "won" : "lost";
  if (type === "KNOCK_OUT") return didTouchSingleBarrier(path, barrier) ? "lost" : "won";
  if (type === "STAYS_IN") return didLeaveRange(path, low, high) ? "lost" : "won";
  if (type === "STAYS_OUT") return didEnterRange(path, low, high) ? "lost" : "won";
  return exitPrice > entry ? "won" : "lost";
}

function didTouchSingleBarrier(path: TradePoint[], barrier: number | undefined): boolean {
  if (typeof barrier !== "number") return false;
  let previous: number | undefined;
  for (const point of path) {
    if (touchesBarrier(point.price, barrier, previous)) return true;
    previous = point.price;
  }
  return false;
}

function didLeaveRange(path: TradePoint[], low: number | undefined, high: number | undefined): boolean {
  if (typeof low !== "number" || typeof high !== "number") return false;
  return path.some((point) => !isWithin(point.price, low, high));
}

function didEnterRange(path: TradePoint[], low: number | undefined, high: number | undefined): boolean {
  if (typeof low !== "number" || typeof high !== "number") return false;
  return path.some((point) => isWithin(point.price, low, high));
}

function resolvePathDependentEarly(state: State, trade: TradeMarker, latestTickTime: number): void {
  if (trade.status !== "active") return;
  const type = normalizeContractType(trade.contractType);
  if (type !== "KNOCK_IN" && type !== "KNOCK_OUT" && type !== "STAYS_IN" && type !== "STAYS_OUT") return;

  const path = getTradePathPoints(state, trade, Math.min(latestTickTime, trade.expiryTime));
  if (type === "KNOCK_IN" && didTouchSingleBarrier(path, trade.barrier)) {
    settleTradeMarker(state, trade.contractId ?? trade.id, { result: "won", exitPrice: path[path.length - 1]?.price });
  } else if (type === "KNOCK_OUT" && didTouchSingleBarrier(path, trade.barrier)) {
    settleTradeMarker(state, trade.contractId ?? trade.id, { result: "lost", exitPrice: path[path.length - 1]?.price });
  } else if (type === "STAYS_IN" && didLeaveRange(path, trade.barrierLow, trade.barrierHigh)) {
    settleTradeMarker(state, trade.contractId ?? trade.id, { result: "lost", exitPrice: path[path.length - 1]?.price });
  } else if (type === "STAYS_OUT" && didEnterRange(path, trade.barrierLow, trade.barrierHigh)) {
    settleTradeMarker(state, trade.contractId ?? trade.id, { result: "lost", exitPrice: path[path.length - 1]?.price });
  }
}

  // Add to trades.ts or a new file
  export function isDigitsCategory(contractType: string): boolean {
    const digitsTypes = [
      "MATCHES", "DIFFERS", "EVEN", "ODD", 
      "PRIME", "NON_PRIME", "OVER", "UNDER", 
      "RANGE_IN", "RANGE_OUT"
    ];
    return digitsTypes.includes(contractType);
  }

// function getEntryMarkerY(
//   state: State,
//   trade: TradeMarker,
//   entryPriceY: number,
//   plotTop: number,
//   plotBottom: number,
// ): number {
//   const intervalMs = getTimeframeIntervalMs(state);
//   const bucketStart = state.timeframe === "1t"
//     ? trade.entryTime
//     : Math.floor(trade.entryTime / intervalMs) * intervalMs;
//   const bucketEnd = bucketStart + Math.max(1_000, intervalMs);

//   let high = trade.entryPrice;
//   let low = trade.entryPrice;
//   for (const tick of state.chartData) {
//     const t = tick.epoch * 1000;
//     if (t < bucketStart || t >= bucketEnd) continue;
//     if (tick.quote > high) high = tick.quote;
//     if (tick.quote < low) low = tick.quote;
//   }

//   const highY = priceToY(state, high);
//   const lowY = priceToY(state, low);
//   const pad = 18;

//   if (trade.direction === "buy") {
//     return clamp(Math.min(entryPriceY, highY) - pad, plotTop + 12, plotBottom - 12);
//   }
//   return clamp(Math.max(entryPriceY, lowY) + pad, plotTop + 12, plotBottom - 12);
// }

function findTrade(state: State, contractIdOrMarkerId: string | number): TradeMarker | undefined {
  const targetId = normalizeContractId(contractIdOrMarkerId);
  return state.trades.find((trade) => {
    if (trade.id === contractIdOrMarkerId) return true;
    const tradeContractId = normalizeContractId(trade.contractId);
    return targetId !== undefined && tradeContractId === targetId;
  });
}

function applyTradeMarkerFields(trade: TradeMarker, input: Partial<ContractMarkerInput>): TradeMarker {
  const contractId = normalizeContractId(input.contractId);
  if (contractId !== undefined) trade.contractId = contractId;
  if (input.contractType !== undefined) trade.contractType = input.contractType;
  if (input.direction !== undefined) trade.direction = input.direction;
  if (input.entryPrice !== undefined && Number.isFinite(input.entryPrice)) trade.entryPrice = input.entryPrice;
  if (input.entryTimeEpoch !== undefined && Number.isFinite(input.entryTimeEpoch)) {
    trade.entryTime = normalizeEpochMs(input.entryTimeEpoch) ?? trade.entryTime;
  }
  if (input.barrier !== undefined && Number.isFinite(input.barrier)) trade.barrier = input.barrier;
  if (input.barrierHigh !== undefined && Number.isFinite(input.barrierHigh)) trade.barrierHigh = input.barrierHigh;
  if (input.barrierLow !== undefined && Number.isFinite(input.barrierLow)) trade.barrierLow = input.barrierLow;
  if (input.digitTarget !== undefined && Number.isFinite(input.digitTarget)) trade.digitTarget = Math.trunc(input.digitTarget);
  if (input.digitLow !== undefined && Number.isFinite(input.digitLow)) trade.digitLow = Math.trunc(input.digitLow);
  if (input.digitHigh !== undefined && Number.isFinite(input.digitHigh)) trade.digitHigh = Math.trunc(input.digitHigh);
  if (input.duration !== undefined && Number.isFinite(input.duration) && input.duration > 0) {
    const durationUnit = normalizeDurationUnit(input.durationUnit);
    trade.durationValue = Math.max(1, Math.floor(input.duration));
    trade.durationUnit = durationUnit;
    trade.durationMs = durationToMs(trade.durationValue, durationUnit);
  }
  if (input.expiryTimeEpoch !== undefined && Number.isFinite(input.expiryTimeEpoch)) {
    trade.expiryTime = normalizeEpochMs(input.expiryTimeEpoch) ?? trade.expiryTime;
  }
  return trade;
}

export function addContractMarker(state: State, input: ContractMarkerInput): TradeMarker {
  validateContractMarker(input);

  const last = state.chartData[state.chartData.length - 1];
  const now = Date.now();
  const entryPrice = input.entryPrice ?? last?.quote;
  if (typeof entryPrice !== "number" || !Number.isFinite(entryPrice)) {
    throw new Error("Contract marker requires entryPrice or current chart data.");
  }

  const entryTime = normalizeEpochMs(input.entryTimeEpoch) ?? (last ? last.epoch * 1000 : now);
  const durationUnit = normalizeDurationUnit(input.durationUnit);
  const durationMs = durationToMs(input.duration, durationUnit);
  const expiryTime = normalizeEpochMs(input.expiryTimeEpoch) ?? entryTime + durationMs;
  const direction = input.direction ?? (normalizeContractType(input.contractType) === "DOWN" || normalizeContractType(input.contractType) === "FALL" ? "sell" : "buy");

  // Convert offset barriers to absolute values
  let barrier = input.barrier;
  let barrierLow = input.barrierLow;
  let barrierHigh = input.barrierHigh;
  
  // Helper to convert offset to absolute if needed
  const convertBarrier = (value: number | undefined, entryPrice: number): number | undefined => {
    if (value === undefined) return undefined;
    // If value is negative OR very small compared to entry price (< 1%), treat as offset
    if (value < 0 || Math.abs(value) < entryPrice * 0.01) {
      return entryPrice + value;
    }
    return value;
  };
  
  barrier = convertBarrier(barrier, entryPrice);
  barrierLow = convertBarrier(barrierLow, entryPrice);
  barrierHigh = convertBarrier(barrierHigh, entryPrice);

  // Auto-switch timeframe for short duration trades (less than 60 seconds)
  if (durationMs < 60000 && state.timeframe !== "1t") {
    // Trigger timeframe change - this will be handled by the caller
    // We'll store a flag that the caller can check
    (state as any).__pendingTimeframeSwitch = { 
      to: "1t", 
      reason: "trade_duration_short",
      durationMs 
    };
  }

  const marker: TradeMarker = {
    id: `tm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    contractId: normalizeContractId(input.contractId),
    contractType: input.contractType,
    direction,
    placedAt: now,
    entryTime,
    entryPrice,
    durationMs,
    durationValue: Math.max(1, Math.floor(input.duration)),
    durationUnit,
    expiryTime,
    barrier: barrier,
    barrierHigh: barrierHigh,
    barrierLow: barrierLow,
    digitTarget: input.digitTarget != null ? Math.trunc(input.digitTarget) : undefined,
    digitLow: input.digitLow != null ? Math.trunc(input.digitLow) : undefined,
    digitHigh: input.digitHigh != null ? Math.trunc(input.digitHigh) : undefined,
    status: "active",
  };

  state.trades.push(marker);
  return marker;
}

export function addTradeMarker(
  state: State,
  direction: TradeDirection,
  duration: number,
  durationUnit: TradeDurationUnit = "s",
): boolean {
  const last = state.chartData[state.chartData.length - 1];
  if (!last) return false;

  addContractMarker(state, {
    contractType: direction === "buy" ? "RISE" : "FALL",
    direction,
    duration,
    durationUnit,
    entryPrice: last.quote,
    entryTimeEpoch: last.epoch * 1000,
  });
  return true;
}

export function activateTradeMarker(
  state: State,
  contractIdOrMarkerId: string | number,
  activation: {
    activationPrice?: number;
    activationTimeEpoch?: number;
    expiryTimeEpoch?: number;
    barrier?: number;
    barrierHigh?: number;
    barrierLow?: number;
    digitTarget?: number;
    digitLow?: number;
    digitHigh?: number;
  },
): TradeMarker | undefined {
  const trade = findTrade(state, contractIdOrMarkerId);
  if (!trade) return undefined;

  // Use backend values directly - no generation
  if (activation.activationPrice !== undefined) {
    trade.activationPrice = activation.activationPrice;
  }
  if (activation.activationTimeEpoch !== undefined) {
    const normalizedTime = normalizeEpochMs(activation.activationTimeEpoch);
    if (normalizedTime !== undefined) {
      trade.activationTime = normalizedTime;
    }
  }
  if (activation.expiryTimeEpoch !== undefined) {
    const normalizedExpiry = normalizeEpochMs(activation.expiryTimeEpoch);
    if (normalizedExpiry !== undefined) {
      trade.expiryTime = normalizedExpiry;
    }
  }
  
  // Convert offset barriers to absolute values using entry price
  const entryPrice = trade.entryPrice;
  const convertBarrier = (value: number | undefined, price: number): number | undefined => {
    if (value === undefined) return undefined;
    if (value < 0 || Math.abs(value) < price * 0.01) {
      return price + value;
    }
    return value;
  };
  
  if (activation.barrier !== undefined) {
    trade.barrier = convertBarrier(activation.barrier, entryPrice);
  }
  if (activation.barrierLow !== undefined) {
    trade.barrierLow = convertBarrier(activation.barrierLow, entryPrice);
  }
  if (activation.barrierHigh !== undefined) {
    trade.barrierHigh = convertBarrier(activation.barrierHigh, entryPrice);
  }
  
  if (typeof activation.digitTarget === "number") trade.digitTarget = Math.trunc(activation.digitTarget);
  if (typeof activation.digitLow === "number") trade.digitLow = Math.trunc(activation.digitLow);
  if (typeof activation.digitHigh === "number") trade.digitHigh = Math.trunc(activation.digitHigh);
  
  return trade;
}

export function settleTradeMarker(
  state: State,
  contractIdOrMarkerId: string | number,
  settlement: {
    exitPrice?: number;
    result?: string;
    settledTimeEpoch?: number;
    settlementTickId?: string;
  },
): TradeMarker | undefined {
  const trade = findTrade(state, contractIdOrMarkerId);
  if (!trade) return undefined;

  // Use backend settled time - don't generate our own
  let settledTime: number;
  if (settlement.settledTimeEpoch !== undefined) {
    settledTime = normalizeEpochMs(settlement.settledTimeEpoch) as number;
  } else {
    // Fallback to chart time only if backend didn't provide (should not happen)
    const last = state.chartData[state.chartData.length - 1];
    settledTime = last ? last.epoch * 1000 : Date.now();
  }
  
  const exitPrice = settlement.exitPrice ?? trade.exitPrice;
  const result = settlement.result?.toLowerCase();

  if (exitPrice !== undefined) trade.exitPrice = exitPrice;
  if (result) {
    trade.status = (result === "win" || result === "won") ? "won" : "lost";
  }
  trade.resolvedAt = settledTime;
  trade.resultSource = settlement.result ? "server" : "chart";
  trade.settlementTickId = settlement.settlementTickId;
  
  return trade;
}

function buildMarkerInputFromLifecycleMessage(message: ContractLifecycleMessage): ContractMarkerInput {
  const duration = message.duration_ticks ?? message.duration ?? 1;
  const durationUnit = message.duration_ticks != null ? "ticks" : (message.duration_unit ?? "s");
  const digitTarget = message.digit_target ?? message.barrier;
  return {
    contractId: normalizeContractId(message.contract_id),
    contractType: message.contract_type ?? "RISE",
    duration,
    durationUnit: durationUnit as TradeDurationUnit,
    entryPrice: message.entry_price ?? message.activation_price,
    entryTimeEpoch: message.entry_time_epoch ?? message.activation_time_epoch,
    expiryTimeEpoch: message.expiry_time_epoch,
    barrier: message.barrier,
    barrierHigh: message.barrier_high,
    barrierLow: message.barrier_low,
    digitTarget: digitTarget != null ? Math.trunc(digitTarget) : undefined,
    digitLow: message.digit_low,
    digitHigh: message.digit_high,
  };
}

function upsertTradeFromLifecycleMessage(state: State, message: ContractLifecycleMessage): TradeMarker {
  const existing = message.contract_id != null ? findTrade(state, message.contract_id) : undefined;
  const input = buildMarkerInputFromLifecycleMessage(message);
  if (existing) {
    return applyTradeMarkerFields(existing, input);
  }
  return addContractMarker(state, input);
}

export function applyContractLifecycleEvent(state: State, event: ContractLifecycleMessage | ContractLifecycleEnvelope): TradeMarker | undefined {
  const message = "message" in event && event.message ? event.message : event as ContractLifecycleMessage;
  const type = message.type;
  if (!type) return undefined;

  if (type === "order_buy") {
    return upsertTradeFromLifecycleMessage(state, message);
  }

  if (type === "contract_activated" && message.contract_id != null) {
    const trade = findTrade(state, message.contract_id);
    if (!trade) {
      return upsertTradeFromLifecycleMessage(state, {
        ...message,
        entry_price: message.entry_price ?? message.activation_price,
        entry_time_epoch: message.entry_time_epoch ?? message.activation_time_epoch,
      });
    }
    return activateTradeMarker(state, message.contract_id, {
      activationPrice: message.activation_price,
      activationTimeEpoch: message.activation_time_epoch,
      expiryTimeEpoch: message.expiry_time_epoch,
      barrier: message.barrier,
      barrierHigh: message.barrier_high,
      barrierLow: message.barrier_low,
      digitTarget: message.digit_target,
      digitLow: message.digit_low,
      digitHigh: message.digit_high,
    });
  }

  if ((type === "contract_settled" || type === "contract_settlement") && message.contract_id != null) {
    const trade = findTrade(state, message.contract_id);
    if (!trade) {
      const created = upsertTradeFromLifecycleMessage(state, {
        ...message,
        entry_price: message.entry_price ?? message.activation_price ?? message.exit_price,
        entry_time_epoch: message.entry_time_epoch ?? message.activation_time_epoch ?? message.settled_time_epoch,
      });
      if (!created) return undefined;
    }
    return settleTradeMarker(state, message.contract_id, {
      exitPrice: message.exit_price,
      result: message.result,
      settledTimeEpoch: message.settled_time_epoch,
      settlementTickId: message.settlement_tick_id,
    });
  }

  return undefined;
}

export function resolveTrades(state: State): void {
  const last = state.chartData[state.chartData.length - 1];
  if (!last) return;

  const latestTickTime = last.epoch * 1000;
  
  for (const trade of state.trades) {
    if (trade.status !== "active") continue;
    
    // Check path-dependent contracts (these can resolve early)
    resolvePathDependentEarly(state, trade, latestTickTime);
    if (trade.status !== "active") continue;
    
    // Only resolve if we have expiry time from backend AND chart time has passed it
    if (trade.expiryTime === undefined) {
      console.warn(`Trade ${trade.contractId} has no expiry time set`);
      continue;
    }
    
    // Check if chart time has passed expiry (using backend-provided expiry)
    if (latestTickTime < trade.expiryTime) continue;
    
    // Trade has expired based on chart time - resolve using chart data
    // This should only happen if backend didn't send settlement event
    const path = getTradePathPoints(state, trade, trade.expiryTime);
    const exitPrice = path[path.length - 1]?.price ?? last.quote;
    trade.exitPrice = exitPrice;
    trade.status = resolveByContractType(trade, exitPrice, path);
    trade.resolvedAt = trade.expiryTime;
    trade.resultSource = "chart";
    
    console.warn(`Trade ${trade.contractId} resolved by chart (no backend settlement)`);
  }
}


function drawBarrier(
  ctx: CanvasRenderingContext2D, 
  state: State, 
  price: number, 
  color: string, 
  label: string,
  isReport: boolean = false,
  barrierIndex: number = 0,
  totalBarriers: number = 1
): void {
  const plotLeft = state.left;
  const plotRight = state.width - state.right;
  const y = priceToY(state, price);
  
  const plotTop = state.top;
  const plotBottom = state.height - state.bottom;
  
  // ===== GET PRECISION FROM THE DATA =====
  const latestTick = state.chartData[state.chartData.length - 1];
  const pip = latestTick?.precision ?? latestTick?.pip_size ?? 2;
  const priceLabel = price.toFixed(pip);

  ctx.save();
  
  // ===== CONDENSED REPORT MODE - Full Names, More Visible =====
  if (isReport) {
    // ===== MAKE LINE MORE VISIBLE =====
    ctx.strokeStyle = rgba(color, 0.7);
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(plotLeft, y);
    ctx.lineTo(plotRight, y);
    ctx.stroke();
    ctx.setLineDash([]);

    // ===== COMPACT BUT FULL LABEL =====
    const labelText = `${label} ${priceLabel}`;
    ctx.font = "bold 10px monospace";
    const textWidth = ctx.measureText(labelText).width;
    const padding = 6;
    const boxHeight = 18;
    
    // ===== POSITION LABEL ON THE LEFT =====
    let boxX = plotLeft + 8;
    let boxY = y - boxHeight / 2;
    
    // If barrier is near top, push label down
    if (y < plotTop + 22) {
      boxY = plotTop + 2;
    }
    // If barrier is near bottom, push label up
    if (y > plotBottom - 22) {
      boxY = plotBottom - boxHeight - 2;
    }
    
    // ===== STACK BARRIERS VERTICALLY (offset Y position) =====
    if (totalBarriers > 1) {
      // Calculate vertical offset to stack labels
      const offsetStep = 20; // Pixels between labels vertically
      const midIndex = (totalBarriers - 1) / 2;
      const offset = (barrierIndex - midIndex) * offsetStep;
      
      // Apply vertical offset
      boxY = boxY + offset;
      
      // Ensure label doesn't go off screen vertically
      if (boxY < plotTop + 2) {
        boxY = plotTop + 2;
      }
      if (boxY + boxHeight > plotBottom - 2) {
        boxY = plotBottom - boxHeight - 2;
      }
    }
    
    // ===== DRAW LABEL WITH MORE VISIBLE BACKGROUND =====
    ctx.fillStyle = rgba(color, 0.9);
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, textWidth + padding * 2, boxHeight, boxHeight / 2);
    ctx.fill();
    
    // ===== BORDER FOR CONTRAST =====
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, textWidth + padding * 2, boxHeight, boxHeight / 2);
    ctx.stroke();
    
    // ===== WHITE TEXT =====
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(labelText, boxX + padding, boxY + boxHeight / 2);
    
  } else {
    // ===== NORMAL VIEW (Bold and prominent) =====
    ctx.strokeStyle = rgba(color, 0.9);
    ctx.lineWidth = 2.5;
    ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(plotLeft, y);
    ctx.lineTo(plotRight, y);
    ctx.stroke();
    ctx.setLineDash([]);

    const labelText = `${label}: ${priceLabel}`;
    ctx.font = "bold 11px monospace";
    const textWidth = ctx.measureText(labelText).width;
    const padding = 8;
    const boxHeight = 22;
    
    let boxX = plotLeft + 10;
    let boxY = y - boxHeight / 2;
    
    if (y < plotTop + 30) {
      boxY = plotTop + 4;
    }
    if (y > plotBottom - 30) {
      boxY = plotBottom - boxHeight - 4;
    }
    
    ctx.fillStyle = rgba(color, 0.95);
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, textWidth + padding * 2, boxHeight, boxHeight / 2);
    ctx.fill();
    
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, textWidth + padding * 2, boxHeight, boxHeight / 2);
    ctx.stroke();
    
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(labelText, boxX + padding, boxY + boxHeight / 2);
  }
  
  ctx.restore();
}

export function renderTrades(ctx: CanvasRenderingContext2D, state: State): void {
  if (state.trades.length === 0) return;

  resolveTrades(state);

  // ===== CHECK IF THIS IS A REPORT =====
  const isReport = state.preserveAllTrades === true;

  // ===== FOR REPORTS: AUTO-ZOOM TO SHOW FULL TRADE DURATION =====
  if (isReport) {
    let earliestTime = Infinity;
    let latestTime = -Infinity;
    let hasTradeData = false;
    
    for (const trade of state.trades) {
      // Find the earliest entry time
      if (trade.entryTime < earliestTime) {
        earliestTime = trade.entryTime;
        hasTradeData = true;
      }
      // Find the latest resolved/exit time
      const exitTime = trade.resolvedAt || trade.expiryTime || trade.entryTime + trade.durationMs;
      if (exitTime > latestTime) {
        latestTime = exitTime;
        hasTradeData = true;
      }
    }
    
    if (hasTradeData && earliestTime < latestTime) {
      const durationMs = latestTime - earliestTime;
      const paddingMs = Math.max(durationMs * 0.2, 2000); // 20% padding or 2 seconds minimum
      const totalRange = durationMs + paddingMs * 2;
      
      // Only adjust if the current view is significantly larger than the trade duration
      const currentRange = state.timeEnd - state.timeStart;
      const maxAllowedRange = Math.max(totalRange * 3, 60000); // Don't zoom in too much
      
      if (currentRange > maxAllowedRange || currentRange > totalRange * 5) {
        // Zoom in to show the full trade with padding
        const newStart = earliestTime - paddingMs;
        const newEnd = latestTime + paddingMs;
        
        // Ensure minimum range
        const newRange = newEnd - newStart;
        if (newRange < 5000) { // Minimum 5 seconds
          const mid = (newStart + newEnd) / 2;
          state.timeStart = mid - 2500;
          state.timeEnd = mid + 2500;
        } else {
          state.timeStart = newStart;
          state.timeEnd = newEnd;
        }
        
        // Mark static dirty to redraw
        markStaticDirty();
      }
    }
  }

  // ===== AUTO-CLEANUP ORPHANED TRADES =====
  const now = Date.now();
  const maxOrphanAge = 30000;
  
  const beforeCount = state.trades.length;
  state.trades = state.trades.filter((trade) => {
    if (trade.status === "won" || trade.status === "lost") {
      return true;
    }
    if (trade.status === "active" && trade.activationTime !== undefined) {
      return true;
    }
    if (trade.status === "active" && trade.activationTime === undefined) {
      const age = now - trade.entryTime;
      if (age > maxOrphanAge) {
        console.warn(`Removing orphaned trade ${trade.id} (age: ${age}ms)`);
        return false;
      }
      return true;
    }
    return true;
  });
  
  const orphansCleaned = beforeCount - state.trades.length;
  if (orphansCleaned > 0) {
    console.log(`Auto-cleaned ${orphansCleaned} orphaned trades`);
  }

  if (!state.preserveAllTrades) {
    state.trades = state.trades.filter((trade) => {
      if (trade.status === "active" || trade.resolvedAt == null) return true;
      return now - trade.resolvedAt <= RESULT_LINGER_MS;
    });
  }

  const plotLeft = state.left;
  const plotTop = state.top;
  const plotRight = state.width - state.right;
  const plotBottom = state.height - state.bottom;

  // ===== COLLECT ALL BARRIERS =====
  const allBarriers: Array<{ price: number; label: string; color: string }> = [];

  for (const trade of state.trades) {
    if (typeof trade.barrier === "number") {
      allBarriers.push({ price: trade.barrier, label: "BARRIER", color: "#f59e0b" });
    }
    if (typeof trade.barrierLow === "number") {
      allBarriers.push({ price: trade.barrierLow, label: "LOW", color: "#f59e0b" });
    }
    if (typeof trade.barrierHigh === "number") {
      allBarriers.push({ price: trade.barrierHigh, label: "HIGH", color: "#f59e0b" });
    }
  }

  // ===== REMOVE DUPLICATE BARRIERS =====
  const uniqueBarriers = allBarriers.filter((barrier, index, self) => 
    index === self.findIndex(b => Math.abs(b.price - barrier.price) < 0.0001)
  );

  // ===== SORT BARRIERS BY PRICE =====
  uniqueBarriers.sort((a, b) => a.price - b.price);

  // ===== EXPAND PRICE RANGE TO INCLUDE BARRIERS (FOR REPORTS) =====
  if (isReport && uniqueBarriers.length > 0) {
    let minPrice = state.priceMin;
    let maxPrice = state.priceMax;
    let needsUpdate = false;
    
    for (const barrier of uniqueBarriers) {
      if (barrier.price < minPrice) {
        minPrice = barrier.price;
        needsUpdate = true;
      }
      if (barrier.price > maxPrice) {
        maxPrice = barrier.price;
        needsUpdate = true;
      }
    }
    
    if (needsUpdate) {
      const range = maxPrice - minPrice;
      const padding = getAutoPricePadding(state, range);
      state.priceMin = minPrice - padding;
      state.priceMax = maxPrice + padding;
      
      // Ensure minimum range
      const minimumRange = state.tightPriceScale ? getMinimumAutoPriceRange(state) : 0.5;
      if (state.priceMax - state.priceMin < minimumRange) {
        const mid = (state.priceMin + state.priceMax) / 2;
        state.priceMin = mid - minimumRange / 2;
        state.priceMax = mid + minimumRange / 2;
      }
      
      // Mark static dirty to redraw
      markStaticDirty();
    }
  }

  // ===== DRAW BARRIERS WITH REPORT MODE AND STACKING =====
  for (let i = 0; i < uniqueBarriers.length; i++) {
    const barrier = uniqueBarriers[i];
    drawBarrier(
      ctx, 
      state, 
      barrier.price, 
      barrier.color, 
      barrier.label, 
      isReport,
      i,
      uniqueBarriers.length
    );
  }

  // ===== DRAW TRADE ELEMENTS WITH CLIPPING =====
  ctx.save();
  ctx.beginPath();
  ctx.rect(plotLeft, plotTop, plotRight - plotLeft, plotBottom - plotTop);
  ctx.clip();

  for (const trade of state.trades) {
    // ===== ENTRY POINT =====
    const entryX = timeToX(state, trade.entryTime);
    const entryY = priceToY(state, trade.entryPrice);
    
    const hasActivation = trade.activationTime !== undefined && trade.activationPrice !== undefined;
    const activationX = hasActivation ? timeToX(state, trade.activationTime!) : null;
    const activationY = hasActivation ? priceToY(state, trade.activationPrice!) : null;
    
    const hasExit = trade.exitPrice !== undefined && trade.resolvedAt !== undefined;
    const exitX = hasExit ? timeToX(state, trade.resolvedAt!) : null;
    const exitY = hasExit ? priceToY(state, trade.exitPrice!) : null;
    
    const directionColor = trade.direction === "buy" ? GREEN : RED;
    const resultColor = trade.status === "won" ? GREEN : RED;
    const activeColor = trade.status === "active" ? directionColor : resultColor;
    
    // Build path from entry to exit
    const latestDataMs = state.chartData[state.chartData.length - 1]?.epoch
      ? state.chartData[state.chartData.length - 1].epoch * 1000
      : Date.now();
      
    let pathEndTime: number;
    if (trade.status === "active") {
      pathEndTime = Math.min(latestDataMs, trade.expiryTime || latestDataMs);
    } else {
      pathEndTime = trade.resolvedAt || trade.expiryTime || latestDataMs;
    }
    
    const path = getTradePathPoints(state, trade, pathEndTime);
    const style = state.tradeStyles;

    if (entryX < plotLeft - 20 || entryX > plotRight + 20) continue;

    // ===== 1. ENTRY DOT =====
    ctx.save();
    const glowRadius = 20;
    const gradient = ctx.createRadialGradient(entryX, entryY, 0, entryX, entryY, glowRadius);
    gradient.addColorStop(0, rgba("#f59e0b", 0.25));
    gradient.addColorStop(1, rgba("#f59e0b", 0));
    ctx.beginPath();
    ctx.arc(entryX, entryY, glowRadius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(entryX, entryY, 7, 0, Math.PI * 2);
    ctx.fillStyle = "#f59e0b";
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(entryX, entryY, 3, 0, Math.PI * 2);
    ctx.fillStyle = "#fbbf24";
    ctx.fill();
    ctx.restore();

    // ===== 2. ENTRY VERTICAL LINE =====
    if (style.vertical) {
      ctx.save();
      ctx.strokeStyle = "rgba(148, 163, 184, 0.5)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(entryX, plotTop);
      ctx.lineTo(entryX, plotBottom);
      ctx.stroke();
      ctx.restore();
    }

    // ===== 3. ACTIVATION =====
    if (hasActivation && activationX !== null && activationY !== null) {
      if (style.vertical) {
        ctx.save();
        ctx.strokeStyle = rgba("#22c55e", 0.5);
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(activationX, plotTop);
        ctx.lineTo(activationX, plotBottom);
        ctx.stroke();
        ctx.restore();
      }
      
      if (style.pin) {
        drawMapPin(ctx, activationX, activationY, trade.direction, "#22c55e");
      }
    }

    // ===== 4. EXIT =====
    if (hasExit && exitX !== null && exitY !== null) {
      if (style.vertical) {
        ctx.save();
        ctx.strokeStyle = rgba(resultColor, 0.5);
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 6]);
        ctx.beginPath();
        ctx.moveTo(exitX, plotTop);
        ctx.lineTo(exitX, plotBottom);
        ctx.stroke();
        ctx.restore();
      }
      
      drawChequeredFlag(ctx, exitX, exitY, resultColor, trade.status);
    }

    // ===== 5. ZONE FILL =====
    if (style.zone && path.length >= 2) {
      const firstX = timeToX(state, path[0].timeMs);
      const firstY = priceToY(state, path[0].price);
      const lastPoint = path[path.length - 1];
      const lastX = timeToX(state, lastPoint.timeMs);
      const baselineY = plotBottom;
      
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(firstX, firstY);
      for (const pt of path) {
        ctx.lineTo(timeToX(state, pt.timeMs), priceToY(state, pt.price));
      }
      ctx.lineTo(lastX, baselineY);
      ctx.lineTo(firstX, baselineY);
      ctx.closePath();

      const zone = ctx.createLinearGradient(0, firstY, 0, baselineY);
      zone.addColorStop(0, rgba(activeColor, 0.15));
      zone.addColorStop(0.5, rgba(activeColor, 0.06));
      zone.addColorStop(1, rgba(activeColor, 0.01));
      ctx.fillStyle = zone;
      ctx.fill();
      ctx.restore();
    }

    // ===== 6. PRICE PATH LINE =====
    if (path.length >= 2) {
      ctx.save();
      ctx.beginPath();
      const firstPoint = path[0];
      ctx.moveTo(timeToX(state, firstPoint.timeMs), priceToY(state, firstPoint.price));
      for (let i = 1; i < path.length; i++) {
        const pt = path[i];
        ctx.lineTo(timeToX(state, pt.timeMs), priceToY(state, pt.price));
      }
      
      ctx.strokeStyle = "rgba(148, 163, 184, 0.5)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
  }

  ctx.restore();
}

function drawMapPin(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  direction: TradeDirection,
  color: string = "#22c55e"
): void {
  const R = 10;
  const STEM = 8;
  const cx = x;
  const cy = y - STEM - R;

  // Shadow
  ctx.shadowColor = rgba(color, 0.35);
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;

  // Circle body
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fillStyle = rgba(color, 0.92);
  ctx.fill();

  // Triangle stem pointing DOWN to price level
  ctx.beginPath();
  ctx.moveTo(cx - 5, cy + R - 2);
  ctx.lineTo(cx + 5, cy + R - 2);
  ctx.lineTo(cx, y);
  ctx.closePath();
  ctx.fillStyle = rgba(color, 0.92);
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // White ring
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.4)";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Direction arrow inside (white)
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.font = "bold 10px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(direction === "buy" ? "▲" : "▼", cx, cy);
}

export function drawVerticalDigitIndicator(
  ctx: CanvasRenderingContext2D,
  state: State,
  currentPrice: number,
  _contractType?: string,
  _digitTarget?: number,
  _digitLow?: number,
  _digitHigh?: number,
  isSettled: boolean = false
): void {
  // Check if we have a settled digit to display
  const latestTick = state.chartData[state.chartData.length - 1];
  const displayPrice = latestTick?.quote ?? currentPrice;
  const displayPipSize = latestTick?.precision ?? latestTick?.pip_size ?? 3;
  let displayIsSettled = isSettled;
  let displayResult: "won" | "lost" | null = null;
  
  // If there's settled digit data and it's still valid (within 2 seconds)
  if (settledDigitData !== null) {
    const elapsed = Date.now() - settledDigitData.settledAt;
    if (elapsed <= 2000) {
      displayIsSettled = true;
      displayResult = settledDigitData.result;
    } else {
      settledDigitData = null;
    }
  }
  
  const lastDigit = getFinalDigit(displayPrice, displayPipSize);
  const plotTop = state.top;
  const plotBottom = state.height - state.bottom;

  const TOP_MARGIN = 80;
  const LEFT_MARGIN = 40;
  const BOTTOM_MARGIN = 8;

  const ladderHeight = plotBottom - plotTop - TOP_MARGIN - BOTTOM_MARGIN;
  const digitHeight = ladderHeight / 9;
  const radius = Math.max(7, Math.min(12, digitHeight / 2 - 3));
  const safeTop = plotTop + TOP_MARGIN + radius;
  const safeBottom = plotBottom - BOTTOM_MARGIN - radius;
  const totalHeight = safeBottom - safeTop;
  const centerX = state.left + LEFT_MARGIN + radius;

  const t = getTheme(state);

  ctx.save();

  for (let digit = 9; digit >= 0; digit--) {
    const y = safeTop + (9 - digit) * (totalHeight / 9);
    const isCurrent = digit === lastDigit;

    const winColor = t.up;
    const lossColor = t.down;

    if (isCurrent) {
      const activeColor = winColor;

      // Live/current digit: colored highlight tied to the latest price.
      ctx.beginPath();
      ctx.arc(centerX, y, radius + 4, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(activeColor, 0.4);
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(centerX, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = activeColor;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(centerX, y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(activeColor, 0.9);
      ctx.lineWidth = 2.5;
      ctx.stroke();

      const fontSize = Math.max(9, Math.min(12, radius - 1));
      ctx.fillStyle = t.background;
      ctx.font = `bold ${fontSize}px monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(digit.toString(), centerX, y);

      const triY = y + radius + 3;
      ctx.beginPath();
      ctx.moveTo(centerX, triY + 5);
      ctx.lineTo(centerX - 5, triY);
      ctx.lineTo(centerX + 5, triY);
      ctx.closePath();
      ctx.fillStyle = activeColor;
      ctx.fill();
      
      // ===== SETTLED BADGE =====
      if (displayIsSettled) {
        const resultText = displayResult === "won" ? "✓" : "✗";
        const badgeColor = displayResult === "won" ? winColor : lossColor;
        
        ctx.beginPath();
        ctx.arc(centerX, triY + 12, 9, 0, Math.PI * 2);
        ctx.fillStyle = rgba(badgeColor, 0.9);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.3)";
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 12px monospace";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(resultText, centerX, triY + 12);
      }
    } else {
      const neutralRadius = radius + 0.5;
      const fillColor = rgba(t.mutedText, 0.16);
      const borderColor = rgba(t.mutedText, 0.38);
      const textColor = rgba(t.mutedText, 0.84);

      ctx.shadowColor = rgba(t.mutedText, 0.12);
      ctx.shadowBlur = 1.5;

      ctx.beginPath();
      ctx.arc(centerX, y, neutralRadius, 0, Math.PI * 2);
      ctx.fillStyle = fillColor;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(centerX, y, neutralRadius, 0, Math.PI * 2);
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1.7;
      ctx.stroke();

      ctx.shadowBlur = 0;

      const fontSize = Math.max(9, Math.min(12, radius - 1));
      ctx.fillStyle = textColor;
      ctx.font = `bold ${fontSize}px monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(digit.toString(), centerX, y);
    }
  }

  ctx.restore();
}

function drawChequeredFlag(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,           // exit price level — staff base
  color: string,
  status: TradeStatus,
): void {
  const COLS = 3;
  const ROWS = 2;
  const BOX = 7;
  const GAP = 1;
  const FW = COLS * BOX + (COLS - 1) * GAP;   // 23px
  const FH = ROWS * BOX + (ROWS - 1) * GAP;   // 15px
  const STAFF = 10;                           // staff height above price Y

  // Flag top-left: staff is at x, flag body extends to the RIGHT
  const fy = y - STAFF - FH;   // top of flag grid
  const fx = x + 2;            // flag body starts just right of staff line

  // Staff line (vertical)
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, fy);
  ctx.strokeStyle = rgba(color, 0.8);
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Checkered grid (3x2)
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const isColored = (row + col) % 2 === 0;
      const bx = fx + col * (BOX + GAP);
      const by = fy + row * (BOX + GAP);
      ctx.fillStyle = isColored
        ? rgba(color, 0.9)
        : "rgba(255,255,255,0.85)";
      ctx.fillRect(bx, by, BOX, BOX);
    }
  }

  // Border around flag
  ctx.strokeStyle = rgba(color, 0.4);
  ctx.lineWidth = 0.5;
  ctx.strokeRect(fx - 0.5, fy - 0.5, FW + 1, FH + 1);

  // WON/LOST pill above flag (without background, just text as per Sam's request)
  const label = status === "won" ? "WON" : "LOST";
  ctx.fillStyle = color;
  ctx.font = "bold 8px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillText(label, fx + FW / 2, fy - 2);
}

// function roundRect(
//   ctx: CanvasRenderingContext2D,
//   x: number,
//   y: number,
//   w: number,
//   h: number,
//   r: number
// ): void {
//   ctx.beginPath();
//   ctx.moveTo(x + r, y);
//   ctx.lineTo(x + w - r, y);
//   ctx.quadraticCurveTo(x + w, y, x + w, y + r);
//   ctx.lineTo(x + w, y + h - r);
//   ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
//   ctx.lineTo(x + r, y + h);
//   ctx.quadraticCurveTo(x, y + h, x, y + h - r);
//   ctx.lineTo(x, y + r);
//   ctx.quadraticCurveTo(x, y, x + r, y);
//   ctx.closePath();
// }
export function hasActiveTrades(state: State): boolean {
  return state.trades.some((trade) => trade.status === "active");
}
